<template>
  <div class="cover-container w-100 mx-auto" id="main_page" style="overflow: hidden;">
    <!-- A-Frame AR 場景（沿用 ARpage 的 8th Wall SLAM 對位方式）-->
    <a-scene v-if="isAFrameLoaded" ref="arScene" landing-page xrextras-loading xrextras-runtime-error
      renderer="colorManagement:true" xrweb="allowedDevices: any" embedded vr-mode-ui="enabled: false">

      <a-camera id="gameCamera" position="0 8 0" raycaster="objects: .cantap" cursor="fuse: false; rayOrigin: mouse;">
      </a-camera>

      <a-entity light="type: directional; intensity: 0.8; castShadow: false"
        xrextras-attach="target: camera; offset: 8 15 4" position="1 4.3 2.5">
      </a-entity>
      <a-light type="ambient" intensity="0.6"></a-light>

      <!-- 真實地面（不可見）：讓使用者直接點螢幕上看到的地面位置，用 A-Frame 內建 raycaster 求出真正的交點，
           比「鏡頭方向 × 固定距離」的土炮算法更貼合實際環境，對位後比較不會有飄移/懸空的感覺 -->
      <a-box id="gameGround" class="cantap" scale="1000 2 1000" position="0 -0.99 0"
        material="transparent: true; opacity: 0">
      </a-box>
    </a-scene>

    <!-- 對位提示：請使用者直接點選畫面上看到的芒果樹底部位置（不再是固定準心） -->
    <div class="place-hint" v-if="isShowPlaceHint && !isTreePlaced">
      <div class="place-hint-badge">🎯</div>
      <div class="place-hint-text">{{ languageTexts[currentLanguage].placeHint }}</div>
    </div>

    <!-- 遊戲 HUD -->
    <div class="game-hud" v-if="isGameActive">
      <div class="game-hud-row">{{ languageTexts[currentLanguage].scoreLabel }}：{{ score }}</div>
      <div class="game-hud-row">{{ languageTexts[currentLanguage].progressLabel }}：{{ goodCaught }} / {{ winTarget }}</div>
    </div>

    <!-- 籃子（2D 覆蓋層，僅比對水平位置是否重疊，不考慮遠近）：需按住籃子本身才能拖曳左右移動 -->
    <div class="basket-zone" v-if="isGameActive">
      <img :src="basketImg" class="basket" :style="{ left: basketX + 'px', width: basketWidth + 'px' }" alt=""
        @touchstart.prevent="onBasketTouchStart" @mousedown.prevent="onBasketMouseDown">
    </div>

    <!-- 過關畫面 -->
    <div class="finish-page" v-if="isShowWinPage">
      <div class="finish-content">
        <h1>{{ languageTexts[currentLanguage].winTitle }}</h1>
        <p class="finish-score">{{ languageTexts[currentLanguage].winScoreLabel }}：{{ score }}</p>
        <div class="btn_area">
          <div class="btn_css" @click="restartGame">{{ languageTexts[currentLanguage].restartButton }}</div>
          <div class="btn_css" @click="onBackToMap">{{ languageTexts[currentLanguage].backMapButton }}</div>
        </div>
      </div>
    </div>

    <!-- 開發者模式：即時調整芒果掉落相關數值 -->
    <div class="dev-toggle" v-if="devMode" @click="devPanelOpen = !devPanelOpen">⚙</div>
    <div class="dev-panel" v-if="devMode && devPanelOpen">
      <div class="dev-header">芒果掉落調整</div>
      <div class="dev-label">起始高度（公尺，相對樹底）</div>
      <div class="dev-scale-row">
        <button class="dev-btn" @click="devAdjust('devMangoStartHeight', -0.3, 0.2)">−−</button>
        <button class="dev-btn" @click="devAdjust('devMangoStartHeight', -0.1, 0.2)">−</button>
        <span class="dev-value">{{ devMangoStartHeight.toFixed(2) }}</span>
        <button class="dev-btn" @click="devAdjust('devMangoStartHeight', 0.1)">+</button>
        <button class="dev-btn" @click="devAdjust('devMangoStartHeight', 0.3)">++</button>
      </div>
      <div class="dev-label">水平散布範圍（公尺）</div>
      <div class="dev-scale-row">
        <button class="dev-btn" @click="devAdjust('devMangoSpread', -0.2, 0)">−−</button>
        <button class="dev-btn" @click="devAdjust('devMangoSpread', -0.05, 0)">−</button>
        <span class="dev-value">{{ devMangoSpread.toFixed(2) }}</span>
        <button class="dev-btn" @click="devAdjust('devMangoSpread', 0.05)">+</button>
        <button class="dev-btn" @click="devAdjust('devMangoSpread', 0.2)">++</button>
      </div>
      <div class="dev-label">芒果大小（公尺）</div>
      <div class="dev-scale-row">
        <button class="dev-btn" @click="devAdjust('devMangoSize', -0.03, 0.02)">−−</button>
        <button class="dev-btn" @click="devAdjust('devMangoSize', -0.01, 0.02)">−</button>
        <span class="dev-value">{{ devMangoSize.toFixed(3) }}</span>
        <button class="dev-btn" @click="devAdjust('devMangoSize', 0.01)">+</button>
        <button class="dev-btn" @click="devAdjust('devMangoSize', 0.03)">++</button>
      </div>
      <div class="dev-label">掉落時長（毫秒）</div>
      <div class="dev-scale-row">
        <button class="dev-btn" @click="devAdjust('devFallDuration', -400, 500)">−−</button>
        <button class="dev-btn" @click="devAdjust('devFallDuration', -100, 500)">−</button>
        <span class="dev-value">{{ devFallDuration }}</span>
        <button class="dev-btn" @click="devAdjust('devFallDuration', 100)">+</button>
        <button class="dev-btn" @click="devAdjust('devFallDuration', 400)">++</button>
      </div>
      <div class="dev-label">生成間隔下限（毫秒）</div>
      <div class="dev-scale-row">
        <button class="dev-btn" @click="devAdjust('devSpawnIntervalMin', -200, 200)">−−</button>
        <button class="dev-btn" @click="devAdjust('devSpawnIntervalMin', -50, 200)">−</button>
        <span class="dev-value">{{ devSpawnIntervalMin }}</span>
        <button class="dev-btn" @click="devAdjust('devSpawnIntervalMin', 50)">+</button>
        <button class="dev-btn" @click="devAdjust('devSpawnIntervalMin', 200)">++</button>
      </div>
      <div class="dev-label">生成間隔上限（毫秒）</div>
      <div class="dev-scale-row">
        <button class="dev-btn" @click="devAdjust('devSpawnIntervalMax', -200, 200)">−−</button>
        <button class="dev-btn" @click="devAdjust('devSpawnIntervalMax', -50, 200)">−</button>
        <span class="dev-value">{{ devSpawnIntervalMax }}</span>
        <button class="dev-btn" @click="devAdjust('devSpawnIntervalMax', 50)">+</button>
        <button class="dev-btn" @click="devAdjust('devSpawnIntervalMax', 200)">++</button>
      </div>
      <div class="dev-label">好芒果機率（0~1）</div>
      <div class="dev-scale-row">
        <button class="dev-btn" @click="devAdjust('devGoodChance', -0.1, 0, 1)">−−</button>
        <button class="dev-btn" @click="devAdjust('devGoodChance', -0.05, 0, 1)">−</button>
        <span class="dev-value">{{ devGoodChance.toFixed(2) }}</span>
        <button class="dev-btn" @click="devAdjust('devGoodChance', 0.05, 0, 1)">+</button>
        <button class="dev-btn" @click="devAdjust('devGoodChance', 0.1, 0, 1)">++</button>
      </div>
      <div class="dev-label">接住判定線距底部（像素）</div>
      <div class="dev-scale-row">
        <button class="dev-btn" @click="devAdjust('devCatchLineOffset', -30, 20)">−−</button>
        <button class="dev-btn" @click="devAdjust('devCatchLineOffset', -10, 20)">−</button>
        <span class="dev-value">{{ devCatchLineOffset }}</span>
        <button class="dev-btn" @click="devAdjust('devCatchLineOffset', 10)">+</button>
        <button class="dev-btn" @click="devAdjust('devCatchLineOffset', 30)">++</button>
      </div>
      <div class="dev-row">
        <button class="dev-btn dev-save-btn" :disabled="!isTreePlaced" @click="restartGame">重新開始（套用目前數值）</button>
      </div>
    </div>

    <TopNavigation :showIndexBtn="isShowIndexBtn" :showMapBtn="isShowMapBtn" :showDataBtn="false"
      :currentLanguage="currentLanguage" @back-to-index="onBackToIndex" @back-to-map="onBackToMap" />
  </div>
</template>

<script>
import Cookies from 'js-cookie'
import TopNavigation from '@/components/TopNavigation.vue'
import { withBase, getBasePath } from '@/utils/urlBase'
import basketImg from '@/assets/images/bamboo_basket.png'

// 以下這些「掉落 / 判定」相關的預設值都可以在畫面上的開發者面板即時調整，這裡只是初始值
// 芒果掉落動畫時長（毫秒）
const DEFAULT_FALL_DURATION = 2400
// 好芒果／爛芒果的生成比例（好芒果機率）
const DEFAULT_GOOD_CHANCE = 0.65
// 判定「接到」的螢幕 Y 座標與底部的距離（像素）
const DEFAULT_CATCH_LINE_OFFSET = 130
// 芒果從錨點（樹底）正上方多高的地方開始掉落（公尺），模擬從樹冠掉落
const DEFAULT_MANGO_START_HEIGHT = 5.2
// 芒果掉落時的水平散布範圍（公尺）
const DEFAULT_MANGO_SPREAD = 6
// 芒果大小（半徑，公尺）
const DEFAULT_MANGO_SIZE = 0.5
// 兩顆芒果之間的生成間隔範圍（毫秒）
const DEFAULT_SPAWN_INTERVAL_MIN = 1000
const DEFAULT_SPAWN_INTERVAL_MAX = 1800
// 切換為 false 即關閉開發者調整面板
const DEV_MODE = true

export default {
  name: 'MangoGamePage',
  components: {
    TopNavigation,
  },
  data() {
    return {
      isShowIndexBtn: false,
      isShowMapBtn: false,
      isAFrameLoaded: false,
      isShowPlaceHint: false,
      isTreePlaced: false,
      isGameActive: false,
      isShowWinPage: false,
      score: 0,
      goodCaught: 0,
      winTarget: 5,
      basketImg,
      basketX: 0,
      basketWidth: 90,
      indexLink: window.location.protocol + '//' + window.location.host + getBasePath(),
      loadedScripts: [],
      // 開發者模式：即時調整芒果掉落相關數值
      devMode: false,
      devPanelOpen: false,
      devMangoStartHeight: DEFAULT_MANGO_START_HEIGHT,
      devMangoSpread: DEFAULT_MANGO_SPREAD,
      devMangoSize: DEFAULT_MANGO_SIZE,
      devFallDuration: DEFAULT_FALL_DURATION,
      devSpawnIntervalMin: DEFAULT_SPAWN_INTERVAL_MIN,
      devSpawnIntervalMax: DEFAULT_SPAWN_INTERVAL_MAX,
      devGoodChance: DEFAULT_GOOD_CHANCE,
      devCatchLineOffset: DEFAULT_CATCH_LINE_OFFSET,
      languageTexts: {
        zh: {
          placeHint: '請直接點選畫面上芒果樹的底部位置',
          scoreLabel: '分數',
          progressLabel: '好芒果',
          winTitle: '恭喜過關！',
          winScoreLabel: '最終分數',
          restartButton: '再玩一次',
          backMapButton: '返回地圖',
        },
        en: {
          placeHint: 'Tap directly on the base of the mango tree shown on screen',
          scoreLabel: 'Score',
          progressLabel: 'Good Mangoes',
          winTitle: 'You Win!',
          winScoreLabel: 'Final Score',
          restartButton: 'Play Again',
          backMapButton: 'Back to Map',
        },
        bn: {
          placeHint: '画面に映っているマンゴーの木の根元を直接タップしてください',
          scoreLabel: 'スコア',
          progressLabel: '良いマンゴー',
          winTitle: 'クリア！',
          winScoreLabel: '最終スコア',
          restartButton: 'もう一度遊ぶ',
          backMapButton: '地図に戻る',
        }
      }
    }
  },
  computed: {
    currentLanguage() {
      return Cookies.get('lang') || 'zh'
    },
  },
  mounted() {
    if (DEV_MODE) {
      this.devMode = true
    }

    // iOS Safari bfcache 修復：頁面從快取還原時強制重新載入，避免 XR session 衝突
    this._onPageShow = (event) => {
      if (event.persisted) {
        window.location.reload()
      }
    }
    window.addEventListener('pageshow', this._onPageShow)

    this._activeMangoes = []

    this.load8thWallScripts().then(() => {
      this.isAFrameLoaded = true
      this.$nextTick(() => {
        const scene = this.$refs.arScene
        if (!scene) return
        if (scene.hasLoaded) {
          this.onSceneLoaded()
        } else {
          scene.addEventListener('loaded', () => this.onSceneLoaded(), { once: true })
        }
      })
    }).catch(error => {
      console.error('Failed to load 8thWall scripts:', error)
      alert('AR 場景載入失敗，請重新整理頁面')
    })
  },
  beforeUnmount() {
    if (this._onPageShow) {
      window.removeEventListener('pageshow', this._onPageShow)
    }
    this.cleanupARScene()
  },
  methods: {
    onSceneLoaded() {
      this.removeVrModeUi()
      this.setupCameraReadyListener()
      this.setupGroundTapListener()
      setTimeout(() => {
        this.isShowIndexBtn = true
        this.isShowMapBtn = true
      }, 1000)
    },

    // 監聽地面點擊（沿用官方 aframe-world-effects-example 的 tap-place 寫法）：
    // camera 上的 raycaster + cursor 元件會在點擊 .cantap 物件時自動算出交點，
    // 從 event.detail.intersection.point 直接拿到真正對齊地面的世界座標
    setupGroundTapListener() {
      const ground = document.getElementById('gameGround')
      if (!ground) return
      this._onGroundTap = (event) => this.onGroundTap(event)
      ground.addEventListener('click', this._onGroundTap)
    },

    // 保險移除 A-Frame 自動插入的「進入 VR/AR」按鈕（vr-mode-ui="enabled:false" 未必能完全擋下）
    removeVrModeUi() {
      document.querySelectorAll('.a-enter-vr, .a-enter-ar, #vr-mode-ui-container').forEach(el => {
        if (el.parentNode) el.parentNode.removeChild(el)
      })
    },

    // 偵測相機取得權限並實際啟動後才顯示對位提示
    setupCameraReadyListener() {
      if (this._cameraModuleRegistered) return
      const vueInstance = this

      if (typeof XR !== 'undefined' && typeof XR.addCameraPipelineModule === 'function') {
        try {
          XR.addCameraPipelineModule({
            name: 'mango-game-camera-ready',
            onStart: () => {
              vueInstance.isShowPlaceHint = true
            }
          })
          this._cameraModuleRegistered = true
          return
        } catch (e) {
          console.warn('[XR] camera pipeline module 設定失敗，採用延遲 fallback：', e)
        }
      }
      setTimeout(() => {
        vueInstance.isShowPlaceHint = true
      }, 2000)
    },

    // 點擊地面時（僅第一次點擊有效），直接使用 A-Frame cursor 算好的交點作為芒果樹「樹底」錨點，
    // 使用者現在是直接點選畫面上看到的芒果樹底部，而不是對準固定準心
    onGroundTap(event) {
      if (this.isTreePlaced) return
      const point = event.detail && event.detail.intersection ? event.detail.intersection.point : null
      if (!point) return
      this.placeTreeAt(point)
    },

    // 建立一個不可見的錨點，標記樹底位置，作為芒果掉落的參考點（真實的芒果樹由現場鏡頭畫面呈現，不疊加虛擬樹）
    placeTreeAt(point) {
      const scene = this.$refs.arScene
      if (!scene) return

      // 純空的 a-entity 本身沒有幾何/材質可渲染，不需另外設 visible:false
      // （若設為 false，會連同底下掛載的芒果子節點一起被隱藏，導致芒果掉下來卻看不到）
      const anchor = document.createElement('a-entity')
      anchor.setAttribute('position', point)

      scene.appendChild(anchor)
      this._treeAnchorEl = anchor

      this.isTreePlaced = true
      this.isShowPlaceHint = false
      this.startGame()
    },

    startGame() {
      this.score = 0
      this.goodCaught = 0
      this.isShowWinPage = false
      this.isGameActive = true
      this.basketWidth = 90
      this.basketX = Math.max(0, window.innerWidth / 2 - this.basketWidth / 2)
      this.clearActiveMangoes()
      this.startSpawning()
      this.startRenderLoop()
    },

    restartGame() {
      this.startGame()
    },

    // ── 開發者模式：調整芒果掉落相關數值 ─────────────────────────────
    devAdjust(field, delta, min = -Infinity, max = Infinity) {
      this[field] = Math.min(max, Math.max(min, parseFloat((this[field] + delta).toFixed(2))))
    },

    // ── 芒果生成／掉落 ──────────────────────────────────────────────
    // 以下讀取的 devXxx 都可以在開發者面板即時調整
    startSpawning() {
      this.stopSpawning()
      const spawnOne = () => {
        this.spawnMango()
        const min = this.devSpawnIntervalMin
        const max = Math.max(min, this.devSpawnIntervalMax)
        this._spawnTimer = setTimeout(spawnOne, min + Math.random() * (max - min))
      }
      this._spawnTimer = setTimeout(spawnOne, 800)
    },
    stopSpawning() {
      if (this._spawnTimer) {
        clearTimeout(this._spawnTimer)
        this._spawnTimer = null
      }
    },
    spawnMango() {
      if (!this._treeAnchorEl) return
      const isGood = Math.random() < this.devGoodChance
      const offsetX = (Math.random() - 0.5) * this.devMangoSpread
      const offsetZ = (Math.random() - 0.5) * this.devMangoSpread
      // 錨點即為對位時標記的樹底位置，芒果從錨點正上方（樹冠高度）落下，掉到接近錨點（樹底）的高度
      const startY = this.devMangoStartHeight
      const endY = 0
      const fallDuration = this.devFallDuration

      const el = document.createElement('a-sphere')
      el.setAttribute('radius', this.devMangoSize)
      el.setAttribute('color', isGood ? '#F2B441' : '#4A4A2E')
      el.setAttribute('position', `${offsetX} ${startY} ${offsetZ}`)
      el.setAttribute('animation', {
        property: 'position',
        to: `${offsetX} ${endY} ${offsetZ}`,
        dur: fallDuration,
        easing: 'linear'
      })
      this._treeAnchorEl.appendChild(el)

      const record = { el, isGood, resolved: false }
      this._activeMangoes.push(record)

      // 若動畫已結束仍未被判定接到，視為掉落未接到，直接移除
      setTimeout(() => {
        if (!record.resolved) this.resolveMango(record, false)
      }, fallDuration + 200)
    },

    // ── 每幀更新：將芒果的 3D 世界座標投影到螢幕座標，僅比對水平重疊 ──────
    startRenderLoop() {
      const THREE = window.AFRAME && window.AFRAME.THREE
      if (!THREE) return
      const vec = new THREE.Vector3()
      const loop = () => {
        if (!this.isGameActive) return
        this.updateMangoPositions(vec)
        this._rafId = requestAnimationFrame(loop)
      }
      this._rafId = requestAnimationFrame(loop)
    },
    stopRenderLoop() {
      if (this._rafId) {
        cancelAnimationFrame(this._rafId)
        this._rafId = null
      }
    },
    updateMangoPositions(vec) {
      const scene = this.$refs.arScene
      const cameraEl = document.getElementById('gameCamera')
      if (!scene || !cameraEl || !this._activeMangoes.length) return
      const cameraObj = cameraEl.getObject3D('camera')
      const canvas = scene.canvas
      if (!cameraObj || !canvas) return

      const rect = canvas.getBoundingClientRect()
      const catchLineY = rect.top + rect.height - this.devCatchLineOffset

      this._activeMangoes.slice().forEach(record => {
        if (record.resolved) return
        record.el.object3D.getWorldPosition(vec)
        vec.project(cameraObj)
        const screenY = rect.top + (1 - vec.y) / 2 * rect.height
        if (screenY >= catchLineY) {
          const screenX = rect.left + (vec.x + 1) / 2 * rect.width
          const caught = screenX >= this.basketX && screenX <= this.basketX + this.basketWidth
          this.resolveMango(record, caught)
        }
      })
    },

    // 判定芒果的最終結果：caught=false 代表沒接到（不論好壞都不計分）
    resolveMango(record, caught) {
      if (record.resolved) return
      record.resolved = true

      if (caught) {
        if (record.isGood) {
          this.score += 10
          this.goodCaught += 1
        } else {
          this.score = Math.max(0, this.score - 5)
        }
      }

      if (record.el.parentNode) record.el.parentNode.removeChild(record.el)
      this._activeMangoes = this._activeMangoes.filter(m => m !== record)

      if (this.goodCaught >= this.winTarget) {
        this.winGame()
      }
    },
    clearActiveMangoes() {
      if (!this._activeMangoes) {
        this._activeMangoes = []
        return
      }
      this._activeMangoes.forEach(m => {
        if (m.el.parentNode) m.el.parentNode.removeChild(m.el)
      })
      this._activeMangoes = []
    },
    winGame() {
      this.isGameActive = false
      this.isShowWinPage = true
      this.stopSpawning()
      this.stopRenderLoop()
      this.clearActiveMangoes()
    },

    // ── 籃子拖曳（純 2D 螢幕座標，不需考慮遠近）：須按住籃子本身才會開始拖曳 ──
    // 籃子中心直接對齊手指／滑鼠的 X 座標，即時跟隨移動，不做相對位移。
    onBasketTouchStart(e) {
      const touch = e.touches[0]
      if (!touch) return
      this.setBasketCenterX(touch.clientX)
      this._onTouchMoveDrag = (moveEvent) => {
        const t = moveEvent.touches[0]
        if (!t) return
        this.setBasketCenterX(t.clientX)
      }
      this._onTouchEndDrag = () => {
        window.removeEventListener('touchmove', this._onTouchMoveDrag)
        window.removeEventListener('touchend', this._onTouchEndDrag)
      }
      window.addEventListener('touchmove', this._onTouchMoveDrag)
      window.addEventListener('touchend', this._onTouchEndDrag)
    },
    onBasketMouseDown(e) {
      this.setBasketCenterX(e.clientX)
      this._onMouseMoveDrag = (moveEvent) => this.setBasketCenterX(moveEvent.clientX)
      this._onMouseUpDrag = () => {
        window.removeEventListener('mousemove', this._onMouseMoveDrag)
        window.removeEventListener('mouseup', this._onMouseUpDrag)
      }
      window.addEventListener('mousemove', this._onMouseMoveDrag)
      window.addEventListener('mouseup', this._onMouseUpDrag)
    },
    setBasketCenterX(clientX) {
      const x = clientX - this.basketWidth / 2
      this.basketX = Math.min(Math.max(0, x), window.innerWidth - this.basketWidth)
    },

    // ── 場景清理 ────────────────────────────────────────────────────
    cleanupARScene() {
      this.stopSpawning()
      this.stopRenderLoop()
      this.clearActiveMangoes()

      if (this._treeAnchorEl && this._treeAnchorEl.parentNode) {
        this._treeAnchorEl.parentNode.removeChild(this._treeAnchorEl)
      }
      this._treeAnchorEl = null

      const ground = document.getElementById('gameGround')
      if (ground && this._onGroundTap) {
        ground.removeEventListener('click', this._onGroundTap)
      }

      if (this._onMouseMoveDrag) window.removeEventListener('mousemove', this._onMouseMoveDrag)
      if (this._onMouseUpDrag) window.removeEventListener('mouseup', this._onMouseUpDrag)
      if (this._onTouchMoveDrag) window.removeEventListener('touchmove', this._onTouchMoveDrag)
      if (this._onTouchEndDrag) window.removeEventListener('touchend', this._onTouchEndDrag)

      const scene = this.$refs.arScene
      if (scene && scene.hasLoaded) {
        try { scene.pause() } catch (e) { /* ignore */ }
      }
      if (typeof XR !== 'undefined') {
        try { XR.stop() } catch (e) { /* ignore */ }
      }
    },

    onBackToIndex() {
      window.location.href = this.indexLink
    },
    onBackToMap() {
      this.cleanupARScene()
      window.location.href = './mapPage'
    },

    // 載入 8thWall 所需的 scripts（與 ARpage.vue 共用同一組本地檔案）
    load8thWallScripts() {
      return new Promise((resolve, reject) => {
        if (typeof window.AFRAME !== 'undefined') {
          resolve()
          return
        }

        const existingScripts = document.querySelectorAll('script[src*="8thwall"], script[src*="aframe"]')
        if (existingScripts.length > 0) {
          const checkAFrame = setInterval(() => {
            if (typeof window.AFRAME !== 'undefined') {
              clearInterval(checkAFrame)
              resolve()
            }
          }, 100)
          return
        }

        const aframeScript = document.createElement('script')
        aframeScript.src = withBase('/js/external/scripts/8frame-1.5.0.min.js')
        this.loadedScripts.push(aframeScript)

        aframeScript.onload = () => {
          const aframeExtraScript = document.createElement('script')
          aframeExtraScript.src = withBase('/js/external/xrextras/aframe-extras-6.1.1.min.js')
          this.loadedScripts.push(aframeExtraScript)

          aframeExtraScript.onload = () => {
            const xrExtrasScript = document.createElement('script')
            xrExtrasScript.src = withBase('/js/external/xrextras/xrextras.js')
            this.loadedScripts.push(xrExtrasScript)

            xrExtrasScript.onload = () => {
              const landingPageScript = document.createElement('script')
              landingPageScript.src = withBase('/js/external/landing-page/landing-page.js')
              this.loadedScripts.push(landingPageScript)

              landingPageScript.onload = () => {
                const eighthWallScript = document.createElement('script')
                eighthWallScript.src = withBase('/js/external/xr/xr.js')
                eighthWallScript.crossOrigin = 'anonymous'
                eighthWallScript.async = true
                eighthWallScript.setAttribute('data-preload-chunks', 'slam')
                this.loadedScripts.push(eighthWallScript)

                eighthWallScript.onload = () => resolve()
                eighthWallScript.onerror = () => reject(new Error('Failed to load 8thWall Web'))
                document.head.appendChild(eighthWallScript)
              }

              landingPageScript.onerror = () => reject(new Error('Failed to load Landing Page'))
              document.head.appendChild(landingPageScript)
            }

            xrExtrasScript.onerror = () => reject(new Error('Failed to load XR Extras'))
            document.head.appendChild(xrExtrasScript)
          }
          aframeExtraScript.onerror = () => reject(new Error('Failed to load XR Extras'))
          document.head.appendChild(aframeExtraScript)
        }

        aframeScript.onerror = () => reject(new Error('Failed to load A-Frame'))
        document.head.appendChild(aframeScript)
      })
    },
  }
}
</script>

<style scoped>
#main_page {
  height: 100svh;
  overflow: hidden;
  position: relative;
}

.place-hint {
  position: absolute;
  bottom: 15%;
  left: 50%;
  transform: translateX(-50%);
  z-index: 500;
  pointer-events: none;
  text-align: center;
  color: #fff;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.6);
}

.place-hint-badge {
  font-size: 48px;
  animation: bob 1.2s ease-in-out infinite;
  display: inline-block;
}

@keyframes bob {

  0%,
  100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-10px);
  }
}

.place-hint-text {
  margin-top: 8px;
  font-size: 15px;
  max-width: 80vw;
  margin-left: auto;
  margin-right: auto;
}

.game-hud {
  position: fixed;
  top: 90px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 500;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(6px);
  border-radius: 16px;
  padding: 8px 20px;
  color: #fff;
  text-align: center;
}

.game-hud-row {
  font-size: 15px;
  font-family: 'MyFont', sans-serif;
}

.basket-zone {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  height: 160px;
  z-index: 400;
  pointer-events: none;
}

.basket {
  position: absolute;
  bottom: 20px;
  height: auto;
  object-fit: contain;
  pointer-events: auto;
  touch-action: none;
  cursor: grab;
}

.finish-page {
  position: fixed;
  inset: 0;
  z-index: 900;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.6);
}

.finish-content {
  background: #fff;
  border-radius: 20px;
  padding: 32px 24px;
  text-align: center;
  width: 80%;
  max-width: 360px;
}

.finish-content h1 {
  font-size: 22px;
  margin-bottom: 8px;
  color: #1a1a1a;
}

.finish-score {
  font-size: 16px;
  color: #477A17;
  margin-bottom: 20px;
}

.btn_area {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.btn_css {
  background: linear-gradient(180deg, #668C42 0%, #477A17 100%);
  color: #fff;
  border-radius: 999px;
  padding: 12px;
  font-size: 15px;
  font-family: 'MyFont', sans-serif;
  letter-spacing: 1px;
  cursor: pointer;
}

/* ── 開發者模式 ── */
.dev-toggle {
  position: fixed;
  top: 80px;
  left: 10px;
  z-index: 10000;
  width: 36px;
  height: 36px;
  background: rgba(0, 0, 0, 0.55);
  color: white;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  cursor: pointer;
  user-select: none;
}

.dev-panel {
  position: fixed;
  top: 120px;
  left: 10px;
  z-index: 10000;
  background: rgba(0, 0, 0, 0.78);
  color: white;
  border-radius: 12px;
  padding: 12px;
  min-width: 230px;
  font-size: 13px;
  max-height: 60vh;
  overflow-y: auto;
}

.dev-header {
  font-weight: bold;
  font-size: 11px;
  color: #aaa;
  margin-bottom: 10px;
  letter-spacing: 0.5px;
}

.dev-label {
  font-size: 10px;
  color: #888;
  margin-bottom: 3px;
}

.dev-scale-row {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 10px;
}

.dev-value {
  flex: 1;
  text-align: center;
  font-size: 20px;
  font-weight: bold;
  font-variant-numeric: tabular-nums;
}

.dev-btn {
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.25);
  color: white;
  border-radius: 6px;
  padding: 7px 10px;
  font-size: 14px;
  cursor: pointer;
  touch-action: manipulation;
  line-height: 1;
}

.dev-btn:active {
  background: rgba(255, 255, 255, 0.3);
}

.dev-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.dev-row {
  display: flex;
  gap: 6px;
  margin-bottom: 6px;
}

.dev-save-btn {
  background: rgba(67, 106, 178, 0.7);
  flex: 1;
}
</style>

<!-- 非 scoped：A-Frame 的進入 VR/AR 按鈕是由其自身 JS 動態插入 DOM，不帶 Vue 的 scoped 屬性，
     scoped style 選不到，所以另外用全域樣式強制隱藏（雙重保險，JS 端也會嘗試移除該節點）-->
<style>
.a-enter-vr,
.a-enter-ar,
#vr-mode-ui-container {
  display: none !important;
}
</style>
